/**
 * SidebarStep7_FutureClosure - Final Review & Submit (Future Closure Path)
 * 
 * Purpose: Give the user a calm, high-level overview and a single "Submit Claim" action.
 * This prevents accidental submissions and feels professional.
 * 
 * Features:
 *   - Total Demand Amount (prominent display)
 *   - Key Summary (business, event, projection period)
 *   - Calculation Highlights (lost profits, continuing expenses, one-time costs, Monte Carlo)
 *   - Download Links (Pro Forma PDF, Excel, Bundle)
 *   - Single Submit Button → Moves claim to dashboard
 *   - Read-only review - no new inputs
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from 'axios';
import WizardV2Layout from './WizardV2Layout';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { 
  CheckCircle, 
  FileText, 
  Download,
  Building,
  Calendar,
  Calculator,
  BarChart3,
  Loader2,
  AlertTriangle,
  FileSpreadsheet,
  Package,
  ArrowRight,
  Shield,
  TrendingUp,
  TrendingDown,
  Zap,
  Target
} from 'lucide-react';

interface SidebarStep7FutureClosureProps {
  onBack: () => void;
  onComplete: () => void;
  goToStep?: (step: number) => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  claimId?: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SidebarStep7_FutureClosure({ 
  onBack, 
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  claimId
}: SidebarStep7FutureClosureProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();

  // Extract data from wizard state
  const reportId = data.report_id || data.generated_report_id;
  const projectionResults = data.projection_results || data.future_closure_result || {};
  
  // Get projection parameters
  const projectionParams = data.future_closure_params || {};
  // Closure duration comes from Step 2 (data.closure_duration_months) primarily
  const closureDuration = data.closure_duration_months || projectionParams.closure_duration_months || 36;
  const aggression = projectionParams.aggression || 50;
  const useBestBase = projectionParams.use_best_base || false;
  
  // Calculate totals
  const shutdownCosts = data.shutdown_costs || {};
  const restartCosts = data.restart_costs || {};
  const totalShutdown = Object.values(shutdownCosts).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
  const totalRestart = Object.values(restartCosts).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
  const totalOneTimeCosts = totalShutdown + totalRestart;
  
  // Pro forma summary
  const proFormaSummary = projectionResults.pro_forma_summary || {};
  const lostProfits = proFormaSummary.total_lost_profits || proFormaSummary.lost_profits || 0;
  const continuingExpenses = proFormaSummary.total_continuing_expenses || proFormaSummary.continuing_expenses || 0;
  const totalDemand = proFormaSummary.total_demand || (lostProfits + continuingExpenses + totalOneTimeCosts);
  
  // Key details
  const claimantName = data.claimant_name || data.business_name || data.client_name || 'Unknown Business';
  const eventType = data.event_type || 'Forced Closure';
  
  // Monte Carlo results (if available)
  const monteCarloResults = data.monte_carlo_results || projectionResults.monte_carlo;
  const hasMonteCarloData = !!monteCarloResults?.results;
  
  // Aggression level label
  const aggressionLabel = aggression < 30 ? 'Conservative' : aggression < 70 ? 'Moderate' : 'Aggressive';

  // Policy Coverage - for limit validation
  const policyCoverage = data.policy_coverage || data.policy_data || {};
  const claimType = policyCoverage.claim_type || data.claim_type || 'unknown';
  const policyLimit = policyCoverage.combined_limit || policyCoverage.bi_limit || policyCoverage.limits?.aggregate_limit || 0;
  
  // Policy limit validation
  const getPolicyLimitStatus = () => {
    // Skip validation for third-party claims or unknown claim types
    if (claimType === 'third_party' || claimType === 'unknown' || !policyLimit) {
      return null;
    }
    
    const percentage = (totalDemand / policyLimit) * 100;
    const isOverLimit = totalDemand > policyLimit;
    const isNearLimit = percentage >= 80 && !isOverLimit;
    
    if (isOverLimit) {
      return {
        type: 'error' as const,
        title: 'Exceeds Policy Limit',
        message: `Your projected demand (${formatCurrency(totalDemand)}) exceeds the policy coverage limit (${formatCurrency(policyLimit)}) by ${formatCurrency(totalDemand - policyLimit)}.`,
        percentage,
        excess: totalDemand - policyLimit
      };
    }
    
    if (isNearLimit) {
      return {
        type: 'warning' as const,
        title: 'Near Policy Limit',
        message: `Your projected demand is using ${percentage.toFixed(1)}% of the available policy coverage (${formatCurrency(policyLimit)}).`,
        percentage,
        excess: 0
      };
    }
    
    return {
      type: 'success' as const,
      title: 'Within Coverage',
      message: `Your projected demand is within policy limits (${percentage.toFixed(1)}% of ${formatCurrency(policyLimit)}).`,
      percentage,
      excess: 0
    };
  };
  
  const policyLimitStatus = getPolicyLimitStatus();

  // Submit the claim to dashboard
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const token = await getToken();
      
      // Mark report as submitted/completed
      if (reportId) {
        await axios.put(
          `${BACKEND_URL}/api/v1/reports/${reportId}`,
          {
            status: 'completed',
            submitted_at: new Date().toISOString(),
            calculation_mode: 'future_closure',
            total_demand: totalDemand,
            projection_params: projectionParams
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      
      // Log the submission
      logActivity({
        reportId: reportId || 'unknown',
        actionType: 'submit',
        actionDetails: `Submitted Future Closure claim for ${claimantName}`,
        section: 'wizard',
        metadata: { 
          calculation_mode: 'future_closure',
          total_demand: totalDemand,
          closure_duration: closureDuration
        }
      });
      
      // Clear wizard backup
      localStorage.removeItem('wizard_backup');
      localStorage.removeItem('current_draft_id');
      
      setIsSubmitted(true);
      
    } catch (err: any) {
      console.error('Submission failed:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to submit claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'summary',
      label: 'Claim Summary',
      isActive: true,
      isComplete: true,
      onClick: () => document.getElementById('summary-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'details',
      label: 'Key Details',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('details-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'calculation',
      label: 'Calculation Highlights',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('calculation-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'downloads',
      label: 'Available Downloads',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('downloads-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    items.push({ id: 'steps', label: 'All steps complete', status: 'ok' });
    items.push({ id: 'projection', label: `${closureDuration}-month projection`, status: 'ok' });
    items.push({ id: 'demand', label: `Demand: ${formatCurrency(totalDemand)}`, status: totalDemand > 0 ? 'ok' : 'warning' });
    
    // Add policy limit check to QA items
    if (policyLimitStatus) {
      if (policyLimitStatus.type === 'error') {
        items.push({ id: 'policy', label: 'Exceeds policy limit', status: 'warning' });
      } else if (policyLimitStatus.type === 'warning') {
        items.push({ id: 'policy', label: 'Near policy limit', status: 'warning' });
      } else {
        items.push({ id: 'policy', label: 'Within policy limits', status: 'ok' });
      }
    }
    
    return items;
  };

  // Success screen after submission
  if (isSubmitted) {
    return (
      <WizardV2Layout
        currentStep={7}
        highestCompletedStep={highestCompletedStep}
        stepTitle="Claim Submitted"
        stepSubtitle="Your claim has been saved to the dashboard"
        completionPercent={100}
        grade="A"
        calculationMode="future_closure"
        sections={buildSections()}
        qaItems={[{ id: 'submitted', label: 'Claim submitted successfully', status: 'ok' }]}
        isSaving={false}
        lastSaved={lastSaved}
        onSave={onSave}
        goToStep={goToStep}
        onBack={onBack}
        showNext={false}
        claimId={claimId}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Claim Submitted Successfully!</h2>
            <p className="text-gray-600 mb-8">
              Your Future Closure projection for <strong>{claimantName}</strong> has been submitted and saved to your dashboard.
            </p>
            
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-6 text-white mb-8">
              <p className="text-blue-200 text-sm mb-2">Total Demand</p>
              <p className="text-4xl font-bold">{formatCurrency(totalDemand)}</p>
              <p className="text-blue-200 text-sm mt-2">{closureDuration}-month projection • {aggressionLabel}</p>
            </div>
            
            {hasMonteCarloData && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-green-800">
                  <strong>95% Confidence Interval:</strong>{' '}
                  {formatCurrency(monteCarloResults.results.ci_95_low)} - {formatCurrency(monteCarloResults.results.ci_95_high)}
                </p>
              </div>
            )}
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors flex items-center gap-2"
              >
                View Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={onComplete}
                className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
              >
                Create Another Claim
              </button>
            </div>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={7}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Final Review & Submit"
      stepSubtitle="Review your Future Closure projection before submission"
      completionPercent={100}
      grade="A"
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* Ready Banner */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Projection Ready for Submission</h2>
              <p className="text-blue-100">All steps complete. Review the summary below and submit when ready.</p>
            </div>
          </div>
        </div>

        {/* Total Demand Summary */}
        <div id="summary-section" className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-8 text-white text-center">
          <p className="text-green-200 text-sm font-medium mb-2">TOTAL DEMAND</p>
          <p className="text-5xl font-bold mb-2">{formatCurrency(totalDemand)}</p>
          <p className="text-green-200">{closureDuration}-month projection • {aggressionLabel}</p>
        </div>

        {/* Policy Limit Validation Warning */}
        {policyLimitStatus && (
          <div 
            className={`rounded-xl p-5 border-2 ${
              policyLimitStatus.type === 'error' 
                ? 'bg-red-50 border-red-300' 
                : policyLimitStatus.type === 'warning'
                ? 'bg-amber-50 border-amber-300'
                : 'bg-green-50 border-green-300'
            }`}
            data-testid="policy-limit-warning"
          >
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                policyLimitStatus.type === 'error'
                  ? 'bg-red-100'
                  : policyLimitStatus.type === 'warning'
                  ? 'bg-amber-100'
                  : 'bg-green-100'
              }`}>
                {policyLimitStatus.type === 'error' ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : policyLimitStatus.type === 'warning' ? (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                ) : (
                  <Shield className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`font-bold ${
                  policyLimitStatus.type === 'error'
                    ? 'text-red-800'
                    : policyLimitStatus.type === 'warning'
                    ? 'text-amber-800'
                    : 'text-green-800'
                }`}>
                  {policyLimitStatus.type === 'error' ? '⚠️ ' : policyLimitStatus.type === 'warning' ? '⚡ ' : '✅ '}
                  {policyLimitStatus.title}
                </h4>
                <p className={`text-sm mt-1 ${
                  policyLimitStatus.type === 'error'
                    ? 'text-red-700'
                    : policyLimitStatus.type === 'warning'
                    ? 'text-amber-700'
                    : 'text-green-700'
                }`}>
                  {policyLimitStatus.message}
                </p>
                
                {/* Progress bar showing usage */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className={
                      policyLimitStatus.type === 'error'
                        ? 'text-red-600'
                        : policyLimitStatus.type === 'warning'
                        ? 'text-amber-600'
                        : 'text-green-600'
                    }>
                      {policyLimitStatus.percentage.toFixed(1)}% of limit used
                    </span>
                    <span className="text-gray-500">{formatCurrency(policyLimit)} limit</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        policyLimitStatus.type === 'error'
                          ? 'bg-red-500'
                          : policyLimitStatus.type === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(policyLimitStatus.percentage, 100)}%` }}
                    />
                  </div>
                </div>
                
                {policyLimitStatus.type === 'error' && (
                  <p className="text-xs text-red-600 mt-2">
                    💡 Consider adjusting projection parameters or reviewing with your attorney about recovery options.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Monte Carlo CI (if available) */}
        {hasMonteCarloData && (
          <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-4 border-2 border-green-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900">95% Confidence Interval</p>
                  <p className="text-xs text-green-700">Monte Carlo Analysis ({monteCarloResults.iterations?.toLocaleString() || '1,000'} simulations)</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-900">
                  {formatCurrency(monteCarloResults.results.ci_95_low)} - {formatCurrency(monteCarloResults.results.ci_95_high)}
                </p>
                <p className="text-xs text-green-700">P50: {formatCurrency(monteCarloResults.results.p50)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Key Details */}
        <div id="details-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Key Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Business</p>
                <p className="font-semibold text-gray-900">{claimantName}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Event Type</p>
                <p className="font-semibold text-gray-900">{eventType}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Projection Period</p>
                <p className="font-semibold text-gray-900">{closureDuration} months</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Target className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Base Period</p>
                <p className="font-semibold text-gray-900">{useBestBase ? 'Best 36 Months' : 'Last 36 Months'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Highlights */}
        <div id="calculation-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Calculation Highlights</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">Lost Profits</span>
              </div>
              <span className="font-bold text-blue-900">{formatCurrency(lostProfits)}</span>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-amber-600" />
                <span className="text-gray-700">Continuing Expenses</span>
              </div>
              <span className="font-bold text-amber-900">{formatCurrency(continuingExpenses)}</span>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">One-Time Costs</span>
              </div>
              <span className="font-bold text-blue-900">{formatCurrency(totalOneTimeCosts)}</span>
            </div>
            
            {hasMonteCarloData && (
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Monte Carlo P50 (Median)</span>
                </div>
                <span className="font-bold text-green-900">{formatCurrency(monteCarloResults.results.p50)}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border-2 border-green-300">
              <span className="font-bold text-gray-900">Total Demand</span>
              <span className="text-xl font-bold text-green-900">{formatCurrency(totalDemand)}</span>
            </div>
          </div>
          
          {/* Aggression indicator */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Projection Stance</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                aggression < 30 ? 'bg-blue-100 text-blue-700' :
                aggression < 70 ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {aggressionLabel} ({aggression}%)
              </span>
            </div>
          </div>
        </div>

        {/* Available Downloads */}
        <div id="downloads-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Available Downloads</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => goToStep?.(6)}
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <FileText className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">Pro Forma Report</p>
                <p className="text-xs text-gray-500">PDF • Full projection</p>
              </div>
            </button>
            
            <button
              onClick={() => goToStep?.(6)}
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">Excel Export</p>
                <p className="text-xs text-gray-500">XLSX • Editable</p>
              </div>
            </button>
            
            <button
              onClick={() => goToStep?.(6)}
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <Package className="w-6 h-6 text-indigo-600" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">Complete Bundle</p>
                <p className="text-xs text-gray-500">All formats</p>
              </div>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Submission Failed</p>
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Submitting Claim...
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                Submit Claim to Dashboard
              </>
            )}
          </button>
          <p className="text-center text-sm text-gray-500 mt-3">
            This will save your completed Future Closure projection to the dashboard.
          </p>
        </div>

      </div>
    </WizardV2Layout>
  );
}
